import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, FileUp, FileText, Megaphone, Eye, RefreshCw, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { fmtDate } from "../../lib/format";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inputCls = "w-full border border-navy/20 bg-white px-4 py-3 text-sm text-navy focus:outline-none focus:border-rouge transition-colors duration-300";
const labelCls = "block font-heading uppercase tracking-wider text-xs text-navy/70 mb-1.5";

const EMPTY_POST = { title: "", content: "", date: "", published: true };

const DOC_TYPES = [
  { value: "reglement_interieur", label: "Règlement intérieur (obligatoire inscription)" },
  { value: "fiche_sante", label: "Fiche santé (obligatoire mineurs)" },
  { value: "autorisation_parentale", label: "Autorisation parentale (obligatoire mineurs)" },
  { value: "statuts", label: "Statuts du club" },
  { value: "licence", label: "Licence" },
  { value: "attestation", label: "Attestation" },
  { value: "certificat", label: "Certificat" },
  { value: "convocation", label: "Convocation" },
  { value: "administratif", label: "Document administratif" },
  { value: "equipe", label: "Document d'équipe" },
  { value: "autre", label: "Autre document" },
];

const AUDIENCES = [
  { value: "tous", label: "Commun à tous les licenciés" },
  { value: "categorie", label: "Réservé à une catégorie" },
  { value: "equipe", label: "Réservé à une équipe" },
  { value: "adherent", label: "Réservé à un licencié précis (document personnel)" },
];

const EMPTY_DOC = {
  title: "", description: "", document_type: "autre", audience: "tous",
  audience_categorie: "", audience_equipe: "", adherent_id: "",
  saison: "", version: "", date_effet: "", published: true,
};

const TYPE_LABELS = Object.fromEntries(DOC_TYPES.map((t) => [t.value, t.label.split(" (")[0]]));
const AUDIENCE_LABELS = { tous: "Tous les licenciés", categorie: "Catégorie", equipe: "Équipe", adherent: "Licencié précis" };

export default function AdminLicencies() {
  const [documents, setDocuments] = useState([]);
  const [posts, setPosts] = useState([]);
  const [adherents, setAdherents] = useState([]);
  const [busy, setBusy] = useState(true);
  const [docForm, setDocForm] = useState(EMPTY_DOC);
  const [docFile, setDocFile] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const replaceRef = useRef(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [d, p, a] = await Promise.all([api.get("/admin/documents"), api.get("/admin/member-posts"), api.get("/admin/adherents")]);
      setDocuments(d.data);
      setPosts(p.data);
      setAdherents(a.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setDoc = (k) => (e) => setDocForm({ ...docForm, [k]: e.target.value });

  const saveDoc = async (e) => {
    e.preventDefault();
    if (editingDoc === "new" && !docFile) {
      toast.error("Choisissez un fichier");
      return;
    }
    setUploading(true);
    try {
      if (editingDoc === "new") {
        const fd = new FormData();
        Object.entries(docForm).forEach(([k, v]) => fd.append(k, String(v)));
        fd.append("file", docFile);
        await api.post("/admin/documents", fd);
        toast.success("Document ajouté à la bibliothèque licenciés");
      } else {
        await api.put(`/admin/documents/${editingDoc}`, docForm);
        toast.success("Document mis à jour");
      }
      setEditingDoc(null);
      setDocForm(EMPTY_DOC);
      setDocFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const startEditDoc = (doc) => {
    setEditingDoc(doc.id);
    setDocForm({
      title: doc.title || "", description: doc.description || "",
      document_type: doc.document_type || "autre", audience: doc.audience || "tous",
      audience_categorie: doc.audience_categorie || "", audience_equipe: doc.audience_equipe || "",
      adherent_id: doc.adherent_id || "", saison: doc.saison || "",
      version: doc.version || "", date_effet: doc.date_effet || "", published: doc.published !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const togglePublish = async (doc) => {
    try {
      await api.post(`/admin/documents/${doc.id}/publier`, { published: !doc.published });
      toast.success(doc.published ? "Document dépublié" : "Document publié");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const replaceFile = async (file) => {
    if (!file || !replaceTarget) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/documents/${replaceTarget.id}/fichier`, fd);
      toast.success(`« ${replaceTarget.title} » remplacé — pensez à incrémenter la version si le contenu a changé`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReplaceTarget(null);
      if (replaceRef.current) replaceRef.current.value = "";
    }
  };

  const removeDoc = async (doc) => {
    if (!window.confirm(`Supprimer le document « ${doc.title} » ?`)) return;
    try {
      await api.delete(`/admin/documents/${doc.id}`);
      toast.success("Document supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const savePost = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, date: form.date || undefined };
      if (editing === "new") {
        await api.post("/admin/member-posts", payload);
        toast.success("Information publiée");
      } else {
        await api.put(`/admin/member-posts/${editing}`, payload);
        toast.success("Information mise à jour");
      }
      setEditing(null);
      setForm(EMPTY_POST);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removePost = async (p) => {
    if (!window.confirm(`Supprimer « ${p.title} » ?`)) return;
    try {
      await api.delete(`/admin/member-posts/${p.id}`);
      toast.success("Information supprimée");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (busy) {
    return <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10"><div className="flex justify-center py-20"><Loader2 className="animate-spin text-rouge" size={28} /></div></main>;
  }

  return (
    <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10 space-y-14">
      <section>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <FileText size={20} className="text-rouge" />
            <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl">Documents licenciés</h2>
          </div>
          <button
            onClick={() => { setEditingDoc("new"); setDocForm(EMPTY_DOC); setDocFile(null); }}
            data-testid="document-new"
            className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-6 py-3 hover:bg-navy transition-colors duration-300"
          >
            <Plus size={16} /> Ajouter un document
          </button>
        </div>

        {editingDoc && (
          <form onSubmit={saveDoc} data-testid="document-form" className="bg-white border border-navy/10 p-6 sm:p-8 mb-8 grid sm:grid-cols-2 gap-5">
            <h3 className="sm:col-span-2 font-heading uppercase font-semibold tracking-tight text-navy text-xl">
              {editingDoc === "new" ? "Nouveau document" : "Modifier le document"}
            </h3>
            <div>
              <label className={labelCls} htmlFor="doc-title">Titre du document *</label>
              <input id="doc-title" data-testid="document-title" required value={docForm.title} onChange={setDoc("title")} className={inputCls} placeholder="Règlement intérieur, convocation…" />
            </div>
            <div>
              <label className={labelCls} htmlFor="doc-type">Type de document</label>
              <select id="doc-type" data-testid="document-type" value={docForm.document_type} onChange={setDoc("document_type")} className={inputCls}>
                {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="doc-desc">Description (optionnel)</label>
              <input id="doc-desc" data-testid="document-description" value={docForm.description} onChange={setDoc("description")} className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="doc-audience">Audience</label>
              <select id="doc-audience" data-testid="document-audience" value={docForm.audience} onChange={setDoc("audience")} className={inputCls}>
                {AUDIENCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            {docForm.audience === "categorie" && (
              <div>
                <label className={labelCls} htmlFor="doc-aud-cat">Catégorie concernée</label>
                <input id="doc-aud-cat" data-testid="document-audience-categorie" value={docForm.audience_categorie} onChange={setDoc("audience_categorie")} className={inputCls} placeholder="Open, U17…" />
              </div>
            )}
            {docForm.audience === "equipe" && (
              <div>
                <label className={labelCls} htmlFor="doc-aud-eq">Équipe concernée</label>
                <input id="doc-aud-eq" data-testid="document-audience-equipe" value={docForm.audience_equipe} onChange={setDoc("audience_equipe")} className={inputCls} placeholder="route, ecole-de-vélo…" />
              </div>
            )}
            {docForm.audience === "adherent" && (
              <div>
                <label className={labelCls} htmlFor="doc-aud-adh">Licencié concerné</label>
                <select id="doc-aud-adh" data-testid="document-audience-adherent" value={docForm.adherent_id} onChange={setDoc("adherent_id")} className={inputCls} required>
                  <option value="">— Choisir un licencié —</option>
                  {adherents.map((a) => <option key={a.id} value={a.id}>{a.prenom} {a.nom}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className={labelCls} htmlFor="doc-version">Version</label>
              <input id="doc-version" data-testid="document-version" value={docForm.version} onChange={setDoc("version")} className={inputCls} placeholder="2027.1" />
            </div>
            <div>
              <label className={labelCls} htmlFor="doc-effet">Date de mise en vigueur</label>
              <input id="doc-effet" data-testid="document-date-effet" type="date" value={docForm.date_effet} onChange={setDoc("date_effet")} className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="doc-saison">Saison (vide = permanent)</label>
              <input id="doc-saison" data-testid="document-saison" value={docForm.saison} onChange={setDoc("saison")} className={inputCls} placeholder="2027" />
            </div>
            <label className="flex items-center gap-3 text-sm text-navy/80 cursor-pointer self-end pb-3" data-testid="document-published-label">
              <input type="checkbox" checked={docForm.published} onChange={(e) => setDocForm({ ...docForm, published: e.target.checked })} data-testid="document-published" className="h-4 w-4 shrink-0 accent-[#D90429]" />
              Document publié (visible par les licenciés concernés)
            </label>
            {editingDoc === "new" && (
              <div className="sm:col-span-2">
                <label data-testid="document-file-label" className="inline-flex items-center gap-2 border border-dashed border-navy/25 px-5 py-3 cursor-pointer text-sm text-steel hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
                  <FileUp size={15} />
                  {docFile ? docFile.name : "Choisir un fichier (pdf, jpg, png, webp — 8 Mo max)"}
                  <input type="file" data-testid="document-file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
                </label>
              </div>
            )}
            <div className="sm:col-span-2 flex gap-3">
              <button type="submit" disabled={uploading} data-testid="document-upload" className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-7 py-3 hover:bg-navy disabled:opacity-60 transition-colors duration-300">
                {uploading && <Loader2 size={15} className="animate-spin" />}
                {editingDoc === "new" ? "Ajouter le document" : "Enregistrer"}
              </button>
              <button type="button" onClick={() => setEditingDoc(null)} data-testid="document-cancel" className="border border-navy/25 text-navy font-heading uppercase tracking-wider text-sm px-7 py-3 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
                Annuler
              </button>
            </div>
          </form>
        )}

        {documents.length === 0 ? (
          <div className="border border-dashed border-navy/25 px-8 py-12 text-center" data-testid="documents-empty">
            <p className="font-heading uppercase font-semibold tracking-tight text-navy text-xl">Aucun document</p>
            <p className="mt-2 text-sm text-steel">Ajoutez le règlement intérieur ou tout document réservé aux licenciés.</p>
          </div>
        ) : (
          <div className="bg-white border border-navy/10" data-testid="documents-table">
            {documents.map((doc) => (
              <div key={doc.id} className="flex flex-wrap items-center gap-4 px-6 py-4 border-b border-navy/10 last:border-0" data-testid={`document-row-${doc.id.slice(0, 8)}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-heading uppercase font-semibold text-navy">
                    {doc.title}
                    {doc.published === false && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-steel/15 text-steel">Dépublié</span>}
                  </p>
                  <p className="text-xs text-steel mt-1">
                    {TYPE_LABELS[doc.document_type] || "Autre"}
                    {doc.obligatoire_inscription ? " · obligatoire à l'inscription" : ""}
                    {" · "}{AUDIENCE_LABELS[doc.audience || "tous"]}
                    {doc.audience === "adherent" ? ` (${(adherents.find((a) => a.id === doc.adherent_id) || {}).prenom || ""} ${(adherents.find((a) => a.id === doc.adherent_id) || {}).nom || ""})` : ""}
                    {doc.version ? ` · Version ${doc.version}` : ""}
                    {doc.saison ? ` · Saison ${doc.saison}` : " · Permanent"}
                    {" · "}{doc.original_filename} · maj {fmtDate(doc.updated_at || doc.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <a href={`${API_URL}/api/admin/documents/${doc.id}/fichier`} target="_blank" rel="noreferrer" data-testid={`document-view-${doc.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" aria-label="Consulter">
                    <Eye size={16} />
                  </a>
                  <button onClick={() => { setReplaceTarget(doc); replaceRef.current?.click(); }} data-testid={`document-replace-${doc.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" aria-label="Remplacer le fichier" title="Remplacer le fichier (nouvelle version)">
                    <RefreshCw size={16} />
                  </button>
                  <button onClick={() => startEditDoc(doc)} data-testid={`document-edit-${doc.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" aria-label="Modifier">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => togglePublish(doc)} data-testid={`document-publish-${doc.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" aria-label={doc.published ? "Dépublier" : "Publier"}>
                    {doc.published ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  <button onClick={() => removeDoc(doc)} data-testid={`document-delete-${doc.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" aria-label="Supprimer">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <input ref={replaceRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="document-replace-input" onChange={(e) => replaceFile(e.target.files?.[0])} />
      </section>

      <section>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Megaphone size={20} className="text-rouge" />
            <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl">Actualités licenciés</h2>
          </div>
          <button
            onClick={() => { setEditing("new"); setForm(EMPTY_POST); }}
            data-testid="post-new"
            className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-6 py-3 hover:bg-navy transition-colors duration-300"
          >
            <Plus size={16} /> Nouvelle information
          </button>
        </div>

        {editing && (
          <form onSubmit={savePost} data-testid="post-editor" className="bg-white border border-navy/10 p-6 sm:p-8 mb-8 space-y-5">
            <h3 className="font-heading uppercase font-semibold tracking-tight text-navy text-xl">
              {editing === "new" ? "Nouvelle information" : "Modifier l'information"}
            </h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls} htmlFor="post-title">Titre</label>
                <input id="post-title" data-testid="post-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="post-date">Date</label>
                <input id="post-date" data-testid="post-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="post-content">Contenu</label>
                <textarea id="post-content" data-testid="post-content" rows={5} required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={saving} data-testid="post-save" className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:bg-navy disabled:opacity-60 transition-colors duration-300">
                {saving && <Loader2 size={15} className="animate-spin" />}
                Publier
              </button>
              <button type="button" onClick={() => setEditing(null)} data-testid="post-cancel" className="border border-navy/25 text-navy font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
                Annuler
              </button>
            </div>
          </form>
        )}

        {posts.length === 0 ? (
          <div className="border border-dashed border-navy/25 px-8 py-12 text-center" data-testid="posts-empty-admin">
            <p className="font-heading uppercase font-semibold tracking-tight text-navy text-xl">Aucune information</p>
            <p className="mt-2 text-sm text-steel">Publiez ici les informations réservées aux licenciés connectés.</p>
          </div>
        ) : (
          <div className="bg-white border border-navy/10" data-testid="posts-table">
            {posts.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-4 px-6 py-4 border-b border-navy/10 last:border-0" data-testid={`post-row-${p.id.slice(0, 8)}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-heading uppercase font-semibold text-navy">{p.title}</p>
                  <p className="text-xs text-steel">{fmtDate(p.date)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setEditing(p.id); setForm({ title: p.title, content: p.content, date: p.date, published: p.published }); }} data-testid={`post-edit-${p.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" aria-label="Modifier">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => removePost(p)} data-testid={`post-delete-${p.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" aria-label="Supprimer">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
